import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useContent } from '@/hooks/useContent';
import { activeFestivalEditionQuery } from '@/api/queries';
import { Logo, TornScrap, WwProse, Container } from '@/components';

const WW_POINTS: { labelKey: string; descKey: string }[] = [
  { labelKey: 'home_point_nonprofit_label', descKey: 'home_point_nonprofit_desc' },
  { labelKey: 'home_point_help_label', descKey: 'home_point_help_desc' },
  { labelKey: 'home_point_nature_label', descKey: 'home_point_nature_desc' },
  { labelKey: 'home_point_friends_label', descKey: 'home_point_friends_desc' },
];

const compactRange = (startsAt?: string, endsAt?: string) => {
  const s = startsAt ? new Date(startsAt) : null;
  if (!s || Number.isNaN(s.getTime())) return null;
  const dm = (d: Date) =>
    `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
  const e = endsAt ? new Date(endsAt) : null;
  if (e && !Number.isNaN(e.getTime())) {
    return `${dm(s)}–${dm(e)}${e.getFullYear()}`;
  }
  return `${dm(s)}${s.getFullYear()}`;
};

const POINT_COLORS = ['var(--ww-orange)', 'var(--ww-chartreuse)', 'var(--paper-50)'];

export const Home = () => {
  const { t } = useTranslation();
  const { home } = useContent();
  const { data: edition } = useQuery(activeFestivalEditionQuery());

  // Always derived from the active festival edition's start/end dates (set in
  // the admin under the edition's Settings). Placeholder until they're filled in.
  const dateLabel = compactRange(edition?.startsAt, edition?.endsAt) ?? 'XX.XX. – XX.XX.2027';
  const place = (edition?.location ?? 'BRANDENBURG').toUpperCase();
  const saleActive = edition?.festivalSaleState === 'open';

  return (
    <div>
      {/* HERO — torn-paper lockup */}
      <section
        className="ww-grain relative overflow-hidden"
        style={{ background: 'var(--paper-100)' }}>
        <div className="pointer-events-none absolute inset-0 z-0">
          <div
            className="absolute left-0 right-0"
            style={{
              top: 150,
              height: 260,
              background: 'var(--ww-chartreuse)',
              clipPath:
                'polygon(0% 3%,12% 0%,28% 4%,46% 1%,63% 4%,82% 0%,100% 3%,100% 96%,84% 100%,66% 97%,48% 100%,30% 96%,14% 100%,0% 97%)',
              opacity: 0.9,
            }}
          />
        </div>

        <div className="relative z-[2] mx-auto grid w-full max-w-[1180px] grid-cols-1 items-start gap-10 px-5 py-12 sm:px-12 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="min-w-0 overflow-hidden">
            <div className="mb-2 inline-block max-w-full" style={{ transform: 'rotate(-1.5deg)' }}>
              <Logo h={220} className="max-w-full" />
              <div className="ml-1.5 mt-4 flex flex-wrap">
                <span className="bg-ww-charcoal px-4 py-2.5 font-mono text-[17px] font-semibold tracking-[0.05em] text-paper-50">
                  {dateLabel}
                </span>
                <span className="bg-ww-orange px-4 py-2.5 font-mono text-[17px] font-semibold tracking-[0.05em] text-paper-50">
                  {place}
                </span>
              </div>
            </div>
            {/* <p className="mt-6 max-w-[460px] font-sans text-lg leading-relaxed text-ink-soft">
              Ein unabhängiges Festival, getragen von einem Kollektiv aus Freund:innen. Drei Tage
              Musik, Wald und Wahnsinn.
            </p> */}
            {saleActive ? (
              <div className="mt-24 flex flex-wrap gap-4">
                <Link to="/tickets" className="ww-btn ww-btn--primary ww-btn--lg">
                  {t('secure_tickets')} →
                </Link>
                <Link to="/program" className="ww-btn ww-btn--ghost ww-btn--lg">
                  {t('view_lineup')}
                </Link>
              </div>
            ) : (
              <p className="mt-24 max-w-[460px] font-sans text-lg leading-relaxed text-ink-soft">
                {t('gap_year_note')}
              </p>
            )}
          </div>

          <div className="relative mt-2 max-w-full overflow-hidden">
            <TornScrap
              edges={['top', 'bottom']}
              seed={21}
              amp={2.2}
              rotate={2}
              className="ww-grain ww-grain--soft"
              bg="var(--ww-charcoal)"
              color="var(--paper-50)">
              <div className="px-8 pb-10 pt-9">
                <ul className="flex list-none flex-col gap-5 p-0">
                  {WW_POINTS.map(({ labelKey, descKey }, i) => (
                    <li key={labelKey}>
                      <div
                        className="font-disp"
                        style={{
                          fontSize: 36,
                          lineHeight: 0.9,
                          color: POINT_COLORS[i % 3],
                          transform: `rotate(${(i % 2 ? 1 : -1) * 0.8}deg)`,
                        }}>
                        {t(labelKey)}
                      </div>
                      <p className="mt-1.5 font-sans text-sm leading-snug text-paper-200">
                        {t(descKey)}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </TornScrap>
          </div>
        </div>
      </section>

      {/* <Marquee
        items={['Kein Sponsoring', 'Kein Schnickschnack', 'Nur Line-up', 'Lagerfeuer', 'und ihr']}
      /> */}

      {/* CMS intro + supporter CTA */}
      {home ? (
        <Container max={900}>
          <div className="ww-eyebrow" style={{ color: 'var(--ww-orange)' }}>
            {t('what_is_this')}
          </div>
          <WwProse html={home} className="mt-4" />
        </Container>
      ) : null}
    </div>
  );
};
